import { useCallback, useState } from "react";

import useNonce from "@hooks/useNonce";
import debounce from "@utils/debounce";

import { OrderFields } from "../../../constants";
import loadCountries from "../../../services/loadCountries";
import type { TCountry } from "../../../types";
import useOrderFieldsLoading from "../../useOrderFieldsLoading";

type TUpdateCountriesToReturn = {
  countriesTo: TCountry[];
  updateCountriesTo: (countryFrom?: string) => void;
};

const debouncedCountriesToLoading = debounce((func) => func(), 200);

const useUpdateCountriesTo = (initialCountriesTo: TCountry[]): TUpdateCountriesToReturn => {
  const [countriesTo, setCountriesTo] = useState(initialCountriesTo);

  const { setFieldLoading } = useOrderFieldsLoading();

  const { updateNonce, getCurrentNonce } = useNonce();

  const updateCountriesTo = useCallback(
    (countryFrom?: string) => {
      const localNonce = updateNonce();

      if (!countryFrom) {
        setCountriesTo([]);
        setFieldLoading(OrderFields.DESTINATION_COUNTRY, false);
        return;
      }

      setFieldLoading(OrderFields.DESTINATION_COUNTRY, true);

      debouncedCountriesToLoading(async () => {
        if (localNonce !== getCurrentNonce()) {
          return;
        }
        const newCountriesTo = await loadCountries({ countryFrom });
        setCountriesTo(newCountriesTo);
        setFieldLoading(OrderFields.DESTINATION_COUNTRY, false);
      });
    },
    [getCurrentNonce, setFieldLoading, updateNonce]
  );

  return { countriesTo, updateCountriesTo };
};
export default useUpdateCountriesTo;
